import {
  isValidPhoneNumber,
  phoneNumberToE164,
  Popover,
  PopoverCloseIcon,
  PopoverContent,
  PopoverTrigger,
} from '@teamsnap/snap-ui';
import { truncateText } from 'components/common/truncate';
import { validEmail } from 'core/validation';
import { DateFormat, DateService } from 'frontend-toolkit';
import { usePostRegEditDrawer } from 'hooks/postRegEditDrawer';
import * as React from 'react';
import { BackendStaticStrings, EditableQuestions } from '../types';
import './ParticipantInfo.scss';

interface ParticipantInfoItemProps {
  label: string;
  name: string;
  value?: string | null;
  renderBy?: React.FC<LinkTextProps>;
}

const ParticipantInfoItem: React.FC<ParticipantInfoItemProps> = ({
  label,
  name,
  value,
  renderBy = Default,
}) => {
  if (!value) {
    return null;
  }

  const Component = renderBy;

  return (
    <div className="sui-flex sui-body-dense sui-mb-1 sui-gap-1">
      <div className="sui-w-1/4 sui-shrink-0 sui-font-bold sui-text-neutral-text-medium">
        {label}
      </div>
      <div
        className="sui-w-3/4 sui-text-neutral-text sui-pr-2"
        data-testid={`ParticipantInfo--${name}`}
      >
        <Component value={value} />
      </div>
    </div>
  );
};

interface LinkTextProps {
  value: string;
}

const Default: React.FC<LinkTextProps> = ({ value }) => <>{value}</>;

const isUnansweredResponse = (v: string): boolean =>
  v === BackendStaticStrings.NO_RESPONSE;

const EmailLink: React.FC<LinkTextProps & { truncate?: boolean }> = ({
  value,
  truncate,
}) => {
  if (isUnansweredResponse(value) || !validEmail(value)) {
    return <>{value}</>;
  }
  return (
    <a
      href={`mailto:${value}`}
      className="sui-underline"
      target="_blank"
      rel="noreferrer"
    >
      {truncate ? truncateText(value, 30) : value}
    </a>
  );
};

const BirthdateFormatted: React.FC<LinkTextProps> = ({ value }) => {
  if (isUnansweredResponse(value) || !value) {
    return <>{value}</>;
  }
  return (
    <>
      {DateService.format(
        DateService.adjustForTimezone(new Date(value)),
        DateFormat.MM_DD_YYYY
      )}
    </>
  );
};

const PhoneLink: React.FC<LinkTextProps> = ({ value }) => {
  if (isUnansweredResponse(value) || !isValidPhoneNumber(value)) {
    return <>{value}</>;
  }
  const stringE164 = phoneNumberToE164(value);
  return (
    <a
      href={`tel:${stringE164}`}
      className="sui-underline"
      target="_blank"
      rel="noreferrer"
    >
      {stringE164}
    </a>
  );
};

const getAddressString = ({
  city,
  state,
  streetAddress,
  zip,
}: Partial<EditableQuestions['participantInfo']>): string => {
  const blankAddress = [streetAddress, city, state, zip].every(
    (v) => !v || isUnansweredResponse(v)
  );

  if (blankAddress) {
    return '';
  }

  const addressParts = [
    !isUnansweredResponse(streetAddress) ? streetAddress : null,
    !!city && !isUnansweredResponse(city) ? `${city},` : null,
    !isUnansweredResponse(state) ? state : null,
    !isUnansweredResponse(zip) ? zip : null,
  ].filter(Boolean);

  return addressParts.join(' ');
};

const ParticipantInfo = () => {
  const { questions } = usePostRegEditDrawer();
  const { participantInfo } = questions || {};
  if (!participantInfo) {
    return null;
  }

  return (
    <div
      data-testid="participant-info"
      className="sui-w-full sui-flex sui-flex-col sui-gap-1 sui-mb-1 sui-mt-4 sui-px-2"
    >
      <ParticipantInfoItem
        label="Date of birth"
        name="birthdate"
        value={participantInfo?.birthdate}
        renderBy={BirthdateFormatted}
      />
      <ParticipantInfoItem
        label="Gender"
        name="gender"
        value={participantInfo?.gender}
      />
      <ParticipantInfoItem
        label="Email"
        name="email"
        value={participantInfo?.email}
        renderBy={(props) => <EmailLink truncate={false} {...props} />}
      />
      <ParticipantInfoItem
        label="Phone"
        name="phoneNumber"
        value={participantInfo?.phoneNumber}
        renderBy={PhoneLink}
      />
      <ParticipantInfoItem
        label="Address"
        name="address"
        value={getAddressString(participantInfo || {})}
      />
      <GuardianInfo guardianInfo={questions.guardianInfo} />
    </div>
  );
};

interface GuardianInfoProps {
  guardianInfo?: EditableQuestions['guardianInfo'];
}

const GuardianInfo: React.FC<GuardianInfoProps> = ({ guardianInfo }) => {
  if (!guardianInfo || guardianInfo.length === 0) {
    return null;
  }
  // only one for now, hide if all fields are empty
  const first = guardianInfo[0];
  const isAllEmpty = Object.values(first).every(
    (v) => !v || isUnansweredResponse(v)
  );
  if (isAllEmpty) {
    return null;
  }

  return (
    <div className="sui-flex sui-body-dense sui-mb-1 sui-gap-1">
      <div className="sui-w-1/4 sui-shrink-0 sui-font-bold sui-text-neutral-text-medium">
        Guardian
      </div>
      <div
        className="sui-w-3/4 sui-text-neutral-text sui-pr-2"
        data-testid={'ParticipantInfo--guardian'}
      >
        {guardianInfo.map((guardian, index) => {
          const { firstName, lastName } = guardian;
          const name =
            !!firstName &&
            !isUnansweredResponse(firstName) &&
            !!lastName &&
            !isUnansweredResponse(lastName)
              ? `${firstName} ${lastName}`
              : null;
          return (
            <div key={index} className="sui-mr-2">
              <Popover>
                <PopoverTrigger asChild>
                  <span className="sui-underline sui-decoration-dotted">
                    {name || 'Details'}
                  </span>
                </PopoverTrigger>
                <PopoverContent align="start" side="bottom" portal={false}>
                  {name && (
                    <div
                      className="sui-heading-sm sui-mb-2 sui-w-[400px] sui-break-words sui-whitespace-normal"
                      style={{ wordBreak: 'break-word' }}
                    >
                      {name}
                    </div>
                  )}
                  <div className="sui-flex sui-flex-col sui-px-1 sui-w-[400px]">
                    <ParticipantInfoItem
                      label="Relation"
                      name="guardian-relation"
                      value={guardian.relation}
                    />
                    <ParticipantInfoItem
                      label="Date of birth"
                      name="guardian-birthdate"
                      value={guardian?.birthdate}
                      renderBy={BirthdateFormatted}
                    />
                    <ParticipantInfoItem
                      label="Email"
                      name="guardian-email"
                      value={guardian.email}
                      renderBy={(props) => <EmailLink truncate {...props} />}
                    />
                    <ParticipantInfoItem
                      label="Phone"
                      name="guardian-phoneNumber"
                      value={guardian.phoneNumber}
                      renderBy={PhoneLink}
                    />
                    <ParticipantInfoItem
                      label="Address"
                      name="guardian-address"
                      value={getAddressString(guardian)}
                    />
                  </div>
                  <PopoverCloseIcon />
                </PopoverContent>
              </Popover>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ParticipantInfo;
